import { type Action, type ThunkDispatch } from '@reduxjs/toolkit';

import { type ApiResult, UserDataClient } from '@app/client';
import { apiFactory } from '@app/client/apiFactory/api';
import { type ResourceId } from '@app/client/common/structs';
import { type EmojiId } from '@app/client/common/structs/emoji/emoji';
import { UserDataKey } from '@app/client/common/structs/user/user-data';
import { type YatPageItemsResponse } from '@app/client/common/structs/yatPage/yat-page-items';
import { YatPageItems } from '@app/client/resources/yat-pages';
import { processPageItem } from '@app/helpers/yatPage/content/processPageItem';
import { LIQUID_ID_FACES_LENGTH } from '@app/pages/Prototype/LiquidIDSection/config';
import { isContentItem, isContentItemPublished, type LiquidIDItem } from '@app/store/slices/yat-page/types';

import { type AppThunkPromise } from '../deferred-redux-store';
import { type RootState } from '../reducers';
import {
    setIsLoadingItems,
    setLiquidIdLoading,
    setYatPageAssets,
    setYatPageLiquidID,
    setYatPageLiquidIdItemsPersisted,
    setYatPagePaging,
    updateAutoProfile,
} from '../slices';
import { type ContentItem } from '../types/yat-page-actions';

export function getYatPageItems(eid: EmojiId, pageId: string, isPageOwner = false, page = '0') {
    return async function (
        dispatch: ThunkDispatch<RootState, void, Action>,
        getState: () => RootState,
    ): Promise<ApiResult<YatPageItemsResponse>> {
        dispatch(setIsLoadingItems(true));
        return apiFactory()
            .call(YatPageItems.getYatPageItems, {
                query: { limit: 50, page, dir: 'Desc' },
                eid,
                pageId,
                isPageOwner,
            })
            .then((result) => {
                if (result.ok) {
                    let pageItems: ContentItem[] = result.value.data.map(processPageItem).filter(isContentItem);

                    // Filter out items that have not been published (on public view)
                    if (!isPageOwner) pageItems = pageItems.filter(isContentItemPublished);

                    // Sort items based on order property
                    const pageOrder: string = isPageOwner ? 'draftOrder' : 'order';

                    pageItems.sort((a, b) =>
                        a[pageOrder] === b[pageOrder] ? 0 : a[pageOrder] > b[pageOrder] ? 1 : -1,
                    );

                    dispatch(setYatPagePaging(result.value.paging));

                    if (page !== '0') {
                        const { sorted, draft } = getState().yatPage.pageAssets;
                        const currentItems = sorted.map((itemId) => draft[itemId].asset);

                        dispatch(setYatPageAssets([...currentItems, ...pageItems]));
                    } else {
                        dispatch(setYatPageAssets(pageItems));
                    }
                }

                return result;
            })
            .catch((error) => {
                return error;
            })
            .finally(() => dispatch(setIsLoadingItems(false)));
    };
}

export interface UserSettingsResponse {
    value: boolean;
}

export function getUserSettings() {
    return async function (dispatch: ThunkDispatch<RootState, void, Action>): Promise<ApiResult<UserSettingsResponse>> {
        return apiFactory()
            .call(UserDataClient.getUserData, {
                key: UserDataKey.AutoGenerateProfile,
            })
            .then((result) => {
                if (result.ok) {
                    const value = result.value?.value;
                    dispatch(updateAutoProfile(value));
                    return result;
                }
            })
            .catch((error) => {
                return error;
            });
    };
}

type ResponseType = AppThunkPromise<ApiResult<YatPageItemsResponse>>;
type GetLiquidIdCollectionItemsParams = {
    collectionId: ResourceId;
    eid: ResourceId;
    isEditMode?: boolean;
    standalone?: boolean;
    /** pageId can be null for public api / isEditMode: false calls */
    pageId: ResourceId | null;
};
export const getLiquidIdCollectionItems = ({
    collectionId,
    eid,
    isEditMode = false,
    standalone = false,
    pageId = null,
}: GetLiquidIdCollectionItemsParams): ResponseType => {
    return async function (dispatch) {
        // Clear Items to make sure old ones aren't shown during loading time
        let liquidIdItems: LiquidIDItem[] = Array(LIQUID_ID_FACES_LENGTH).fill(undefined);
        dispatch(setYatPageLiquidIdItemsPersisted(liquidIdItems));
        dispatch(setYatPageLiquidID({ items: liquidIdItems, unchanged: true }));

        dispatch(setLiquidIdLoading(true));
        return await apiFactory()
            .call(YatPageItems.fetchLiquidIdCollectionItems, {
                collectionId,
                eid,
                isEditMode,
                pageId,
                standalone,
                value: {},
            })
            .then((result) => {
                if (!result.ok) {
                    // TODO handle result error
                    // dispatchErrorAlert(dispatch, result, 'Fetching liquid id items error');
                }
                if (result.ok) {
                    const liquidIdItemsRaw: ContentItem[] = result.value.data
                        .map(processPageItem)
                        .filter(isContentItem);

                    liquidIdItems = Array(LIQUID_ID_FACES_LENGTH).fill(undefined);

                    for (const item of liquidIdItemsRaw) {
                        const pageOrder: string = isEditMode ? 'draftOrder' : 'order';
                        liquidIdItems[item[pageOrder]] = { ...item } as LiquidIDItem;
                    }

                    dispatch(setYatPageLiquidIdItemsPersisted(liquidIdItems));
                    dispatch(setYatPageLiquidID({ items: liquidIdItems, unchanged: true }));
                }
            })
            .catch((e) => {
                return e;
            })
            .finally(() => dispatch(setLiquidIdLoading(false)));
    };
};
